import { showInputBubble } from './input-bubble';

/**
 * Reglas comunes para los campos de HORA escritos a mano (selector
 * appDatePicker="time"). Misma lógica en el front del cliente (elhubsvoffice_front/src/app/
 * shared/time-input-rules.ts): si se cambia una, cambiar la otra.
 */
export const TIME_MAX_DIGITS = 4;
export const TIME_MAX_LENGTH = 8;

export interface TimeLimits {
  min?: string;
  max?: string;
  /** La hora debe ser POSTERIOR a esta (hora de fin respecto a la de inicio). */
  after?: string;
  /** Exigir :00 o :30 (los selectores de reserva usan intervalos de 30 min). */
  halfHourOnly?: boolean;
}

/**
 * Interpreta "9", "09", "9:30", "9.30", "9h30", "930", "1430", "2 pm",
 * "2:30 p. m." y valida rango, intervalos y que sea posterior a `after`.
 */
export function interpretTime(raw: string, limits: TimeLimits = {}): { value: string } | { error: string } {
  const text = String(raw || '').trim().toLowerCase().replace(/\s+/g, ' ');
  if (!text) return { error: 'Escribe la hora, por ejemplo 9, 9:30 o 2:00 PM.' };
  if (/[^0-9:.h apm]/.test(text)) return { error: 'La hora solo lleva números y AM/PM, por ejemplo 9:30 o 2:00 PM.' };
  if ((text.match(/\d/g) || []).length > TIME_MAX_DIGITS) return { error: 'La hora tiene como máximo 4 dígitos, por ejemplo 2:30 PM.' };
  const suffix = '\\s*(a\\.?\\s*m\\.?|p\\.?\\s*m\\.?)?';
  const match = text.match(new RegExp('^(\\d{1,2})(?:\\s*[:.h]\\s*(\\d{1,2}))?' + suffix + '$'))
    || text.match(new RegExp('^(\\d{1,2})(\\d{2})' + suffix + '$'));
  if (!match) return { error: 'No entendimos esa hora. Escríbela así: 9, 9:30 o 2:00 PM.' };
  let hours = Number(match[1]);
  // Una sola cifra de minutos es la decena: "9:3" = 9:30, "9:0" = 9:00.
  let minutes = match[2] !== undefined ? Number(match[2].length === 1 ? match[2] + '0' : match[2]) : 0;
  // "93" (sin separador, no es una hora válida) = 9:30.
  if (match[2] === undefined && match[1].length === 2 && hours > 23) {
    minutes = Number(match[1][1]) * 10;
    hours = Number(match[1][0]);
  }
  const ampm = match[3];
  if (ampm) {
    if (hours < 1 || hours > 12) return { error: 'Con a. m. o p. m. usa horas del 1 al 12.' };
    if (ampm.startsWith('p') && hours < 12) hours += 12;
    if (ampm.startsWith('a') && hours === 12) hours = 0;
  }
  if (hours > 23 || minutes > 59) return { error: 'Esa hora no existe. Usa horas del 1 al 12 y elige AM o PM.' };
  if (limits.halfHourOnly !== false && minutes !== 0 && minutes !== 30) {
    return { error: 'Solo se permiten horas en punto o y media, por ejemplo 9:00 o 9:30.' };
  }
  const value = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  return checkTimeLimits(value, limits);
}

/** Valida una hora ya normalizada (HH:MM) contra rango y "posterior a". */
export function checkTimeLimits(value: string, limits: TimeLimits): { value: string } | { error: string } {
  const min = limits.min?.slice(0, 5);
  const max = limits.max?.slice(0, 5);
  const after = limits.after?.slice(0, 5);
  if ((min && value < min) || (max && value > max)) {
    return { error: min && max ? `El horario disponible es de ${formatTimeLabel(min)} a ${formatTimeLabel(max)}.` : 'Esa hora está fuera del horario disponible.' };
  }
  if (after && value <= after) return { error: `La hora de fin debe ser posterior a la de inicio (${formatTimeLabel(after)}).` };
  return { value };
}

function formatTimeLabel(value: string): string {
  const [hours, minutes = '00'] = value.split(':');
  const hour = Number(hours);
  return `${hour % 12 || 12}:${minutes} ${hour >= 12 ? 'PM' : 'AM'}`;
}

export const HALF_HOUR_MESSAGE = 'Los minutos solo pueden ser 00 o 30: escribe 3 o 30 para la media hora.';
const HOUR_MESSAGE = 'Esa hora no existe. Usa horas del 1 al 12 y elige AM o PM.';

/**
 * Valida lo que se lleva escrito (aún incompleto) para cortar en el momento
 * minutos como 15, 45 o 4: tras la hora, la primera cifra de los minutos
 * solo puede ser 0 o 3, y la segunda solo 0. Cubre "9:3", "9.30", "9h30",
 * "930", "1430" y un sufijo a. m./p. m. al final. Devuelve el error o null.
 */
export function checkTimePrefix(raw: string): string | null {
  const text = raw.toLowerCase().replace(/\s+/g, '');
  const core = text.replace(/[apm.]*$/, (m) => (/[apm]/.test(m) ? '' : m));
  if (/[apm]/.test(core)) return 'Escribe a. m. o p. m. al final, por ejemplo 2 pm.';
  const sep = core.match(/^(\d*)[:.h](\d*)$/);
  const minuteOk = (mm: string) => mm === '' || mm === '0' || mm === '3' || mm === '00' || mm === '30';
  if (sep) {
    const [, hh, mm] = sep;
    if (hh.length > 2 || (hh && Number(hh) > 23)) return HOUR_MESSAGE;
    if (mm.length > 2 || !minuteOk(mm)) return HALF_HOUR_MESSAGE;
    return null;
  }
  if (!/^\d*$/.test(core)) return null;
  const d = core;
  if (d.length <= 1) return null;
  if (d.length === 2) {
    if (Number(d) <= 23) return null;            // "14" (hora)
    return minuteOk(d[1]) ? null : HALF_HOUR_MESSAGE; // "93" = 9:3
  }
  if (d.length === 3) {
    const asHH = Number(d.slice(0, 2)) <= 23 && minuteOk(d[2]);   // "143" -> 14:3
    const asH = minuteOk(d.slice(1));                            // "930" -> 9:30
    return asHH || asH ? null : (Number(d.slice(0, 2)) > 23 && !asH ? HALF_HOUR_MESSAGE : HALF_HOUR_MESSAGE);
  }
  if (d.length === 4) {
    if (Number(d.slice(0, 2)) > 23) return HOUR_MESSAGE;
    return minuteOk(d.slice(2)) ? null : HALF_HOUR_MESSAGE;
  }
  return null;
}

/**
 * Casillas de hora/minutos del selector desplegable de flatpickr: la de
 * minutos solo acepta 0/00/3/30 y la de horas hasta 23, con burbuja.
 */
export function guardPickerTimeFields(instance: any): void {
  const minute: HTMLInputElement | undefined = instance?.minuteElement;
  const hour: HTMLInputElement | undefined = instance?.hourElement;
  const bind = (el: HTMLInputElement | undefined, ok: (v: string) => boolean, message: string) => {
    if (!el || el.dataset['halfHourGuard']) return;
    el.dataset['halfHourGuard'] = '1';
    let last = el.value;
    el.addEventListener('focus', () => { last = el.value; });
    el.addEventListener('input', () => {
      if (ok(el.value)) { last = el.value; return; }
      el.value = last;
      showInputBubble(el, message);
    });
  };
  bind(minute, (v) => ['', '0', '00', '3', '30'].includes(v), HALF_HOUR_MESSAGE);
  const twelveHour = instance?.config?.time_24hr === false;
  bind(hour, (v) => v === '' || (/^\d{1,2}$/.test(v) && (twelveHour ? Number(v) >= 1 && Number(v) <= 12 : Number(v) <= 23)),
    twelveHour ? 'Usa horas del 1 al 12 y elige a. m. o p. m.' : HOUR_MESSAGE);
}

/**
 * Teclado del campo de hora: solo caracteres válidos y como máximo
 * TIME_MAX_DIGITS dígitos / TIME_MAX_LENGTH caracteres, con burbuja.
 */
export function guardTimeTyping(alt: HTMLInputElement): void {
  if (alt.dataset['timeGuard']) return;
  alt.dataset['timeGuard'] = '1';
  // Sin contador de caracteres ("0/8"): el límite se explica con la burbuja.
  alt.setAttribute('data-no-limit-hint', '');
  let sibling = alt.nextElementSibling;
  while (sibling?.classList.contains('limit-hint')) { const next = sibling.nextElementSibling; sibling.remove(); sibling = next; }
  alt.maxLength = TIME_MAX_LENGTH;
  alt.setAttribute('inputmode', 'numeric');
  alt.setAttribute('data-no-guard', '');
  alt.addEventListener('beforeinput', (event: Event) => {
    const e = event as InputEvent;
    // Lo que se valida con burbuja es SOLO lo que escribe el cliente; los
    // valores que pone el sistema (al volver a la ficha, restaurar una
    // búsqueda...) se aceptan en silencio (ver parseDate en la directiva).
    alt.dataset['userTyped'] = '1';
    if (!e.data) return;
    if (/[^0-9:.h apmAPM]/.test(e.data)) {
      e.preventDefault();
      showInputBubble(alt, 'La hora solo lleva números y AM/PM, por ejemplo 9:30 o 2:00 PM.');
      return;
    }
    const start = alt.selectionStart ?? alt.value.length;
    const end = alt.selectionEnd ?? alt.value.length;
    const next = alt.value.slice(0, start) + e.data + alt.value.slice(end);
    if ((next.match(/\d/g) || []).length > TIME_MAX_DIGITS || next.length > TIME_MAX_LENGTH) {
      e.preventDefault();
      showInputBubble(alt, 'La hora tiene como máximo 4 dígitos, por ejemplo 2:30 PM.');
      return;
    }
    const prefixError = checkTimePrefix(next);
    if (prefixError) {
      e.preventDefault();
      showInputBubble(alt, prefixError);
    }
  });
}
